export type Category = {
  id: string;
  label: string;
};

export type ProductSize = {
  id: string;
  label: string;
  delta: number;
};

export type ProductAddon = {
  id: string;
  label: string;
  price: number;
};

export type Product = {
  id: string;
  name: string;
  category: string;
  description: string;
  basePrice: number;
  image: string;
  badge?: string;
  sizes?: ProductSize[];
  addons?: ProductAddon[];
};

export const pickupPoint = {
  id: "naberezhnaya",
  shortName: "Набережная",
  name: "CoffeeTrack · Набережная",
  address: "Набережная Туры",
  hours: "12:00–01:00",
  eta: "7–10 мин",
  rating: "4,9",

  // Временно используем точку с присланной карты.
  // Перед запуском сеть расставим вручную по факту.
  lat: 57.164624,
  lng: 65.523217,
};

export const categories: Category[] = [
  { id: "popular", label: "Популярное" },
  { id: "coffee", label: "Кофе" },
  { id: "other", label: "Не кофе" },
  { id: "cold", label: "Холодные" },
  { id: "food", label: "Еда" },
];

const coffeeSizes: ProductSize[] = [
  { id: "300", label: "300 мл", delta: 0 },
  { id: "400", label: "400 мл", delta: 40 },
];

const largeDrinkSizes: ProductSize[] = [
  { id: "400", label: "400 мл", delta: 0 },
  { id: "500", label: "500 мл", delta: 50 },
];

const coffeeAddons: ProductAddon[] = [
  { id: "syrup", label: "Сироп", price: 30 },
  {
    id: "alt-milk",
    label: "Альтернативное молоко",
    price: 60,
  },
  {
    id: "extra-shot",
    label: "Доп. эспрессо",
    price: 50,
  },
];

const hotdogAddons: ProductAddon[] = [
  {
    id: "cheese-sauce",
    label: "Сырный соус",
    price: 30,
  },
  {
    id: "jalapeno",
    label: "Халапеньо",
    price: 30,
  },
  {
    id: "beef",
    label: "Говяжья сосиска",
    price: 40,
  },
];

//
// Цены сейчас демонстрационные.
// Перед реальным запуском просто сверим прайс.
//
export const products: Product[] = [
  // ─────────────────────────────
  // КОФЕ
  // ─────────────────────────────

  {
    id: "cappuccino",
    name: "Капучино",
    category: "coffee",
    description:
      "Эспрессо, молоко и плотная молочная пенка",
    basePrice: 190,
    image: "/products/cappuccino.svg",
    badge: "Хит",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  {
    id: "latte",
    name: "Латте",
    category: "coffee",
    description:
      "Мягкий кофейный вкус и больше молока",
    basePrice: 210,
    image: "/products/latte.svg",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  {
    id: "americano",
    name: "Американо",
    category: "coffee",
    description:
      "Классический чёрный кофе на основе эспрессо",
    basePrice: 160,
    image: "/products/americano.svg",
    sizes: coffeeSizes,
    addons: [
      {
        id: "syrup",
        label: "Сироп",
        price: 30,
      },
      {
        id: "extra-shot",
        label: "Доп. эспрессо",
        price: 50,
      },
    ],
  },

  {
    id: "raf",
    name: "Раф",
    category: "coffee",
    description:
      "Сливочный кофе с нежной текстурой",
    basePrice: 240,
    image: "/products/raf.svg",
    badge: "Советуем",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  // ─────────────────────────────
  // НЕ КОФЕ
  // ─────────────────────────────

  {
    id: "cocoa",
    name: "Какао",
    category: "other",
    description:
      "Горячее какао на молоке",
    basePrice: 190,
    image: "/products/cocoa.svg",
    sizes: coffeeSizes,
    addons: [
      {
        id: "syrup",
        label: "Сироп",
        price: 30,
      },
      {
        id: "alt-milk",
        label: "Альтернативное молоко",
        price: 60,
      },
    ],
  },

  {
    id: "tea",
    name: "Чай",
    category: "other",
    description:
      "Горячий ароматный чай",
    basePrice: 170,
    image: "/products/tea.svg",
    sizes: [
      {
        id: "400",
        label: "400 мл",
        delta: 0,
      },
      {
        id: "500",
        label: "500 мл",
        delta: 30,
      },
    ],
  },

  // ─────────────────────────────
  // ХОЛОДНЫЕ
  // ─────────────────────────────

  {
    id: "bumble",
    name: "Бамбл кофе",
    category: "cold",
    description:
      "Эспрессо, цитрусовый сок и лёд",
    basePrice: 260,
    image: "/products/bumble.svg",
    badge: "Топ",
    sizes: largeDrinkSizes,
  },

  {
    id: "iced-latte",
    name: "Айс-латте",
    category: "cold",
    description:
      "Эспрессо, холодное молоко и лёд",
    basePrice: 240,
    image: "/products/iced-latte.svg",
    sizes: largeDrinkSizes,
    addons: coffeeAddons,
  },

  {
    id: "lemonade",
    name: "Лимонад",
    category: "cold",
    description:
      "Освежающий фирменный лимонад со льдом",
    basePrice: 220,
    image: "/products/lemonade.svg",
    badge: "Лето",
    sizes: largeDrinkSizes,
  },

  {
    id: "smoothie",
    name: "Смузи",
    category: "cold",
    description:
      "Холодный фруктово-ягодный напиток",
    basePrice: 260,
    image: "/products/smoothie.svg",
    sizes: largeDrinkSizes,
  },

  {
    id: "juice-bowl",
    name: "Джус-болл",
    category: "cold",
    description:
      "Фирменный фруктовый напиток CoffeeTrack",
    basePrice: 290,
    image: "/products/juice-bowl.svg",
    badge: "Фирменный",
  },

  // ─────────────────────────────
  // ЕДА
  // ─────────────────────────────

  {
    id: "hotdog",
    name: "Хот-дог",
    category: "food",
    description:
      "Сосиска, мягкая булочка, овощи и соусы",
    basePrice: 250,
    image: "/products/hotdog.svg",
    badge: "Хит",
    addons: hotdogAddons,
  },

  {
    id: "frenchdog",
    name: "Френч-дог",
    category: "food",
    description:
      "Горячая сосиска в хрустящей французской булочке",
    basePrice: 260,
    image: "/products/frenchdog.svg",
    addons: hotdogAddons,
  },

  {
    id: "corndog",
    name: "Корн-дог",
    category: "food",
    description:
      "Сосиска в хрустящем кукурузном тесте",
    basePrice: 220,
    image: "/products/corndog.svg",
    badge: "Топ",
  },

  {
    id: "fries",
    name: "Картофель фри",
    category: "food",
    description:
      "Золотистый хрустящий картофель",
    basePrice: 180,
    image: "/products/fries.svg",
    sizes: [
      {
        id: "standard",
        label: "Стандарт",
        delta: 0,
      },
      {
        id: "large",
        label: "Большой",
        delta: 70,
      },
    ],
  },

  {
    id: "nuggets",
    name: "Наггетсы",
    category: "food",
    description:
      "Хрустящие куриные наггетсы",
    basePrice: 230,
    image: "/products/nuggets.svg",
    sizes: [
      {
        id: "6",
        label: "6 шт.",
        delta: 0,
      },
      {
        id: "9",
        label: "9 шт.",
        delta: 90,
      },
    ],
  },

  {
    id: "cheese-sticks",
    name: "Сырные палочки",
    category: "food",
    description:
      "Горячие сырные палочки в хрустящей панировке",
    basePrice: 250,
    image: "/products/cheese-sticks.svg",
    badge: "Советуем",
  },

  {
    id: "shrimp",
    name: "Креветки в кляре",
    category: "food",
    description:
      "Креветки в хрустящем золотистом кляре",
    basePrice: 320,
    image: "/products/shrimp.svg",
  },
];

export const popularIds = [
  "cappuccino",
  "raf",
  "bumble",
  "hotdog",
  "corndog",
  "cheese-sticks",
  "fries",
  "juice-bowl",
];
